import { getApiErrorCode } from '../utils/apiError'
import {
  capturePosthogEvent,
  isPosthogCollectionEnabled,
} from './posthogConsent'
import {
  POSTHOG_EVENT,
  sanitizePosthogProperties,
} from './posthogEventPolicy'

const APP_VERSION = process.env.REACT_APP_VERSION || 'unknown'
const HTTP_WRITE_METHODS = new Set(['delete', 'patch', 'post', 'put'])
const EXPECTED_EMPTY_STATE_CODES = new Set([
  'ATTENDANCE_PROOF_NOT_FOUND',
  'GOAL_NOT_FOUND',
  'SESSIONAL_PLAN_NOT_FOUND',
])

const SCREEN_RULES = Object.freeze([
  { pattern: /^\/home\/?$/, feature: 'home', screen: 'home' },
  { pattern: /^\/plans\/?$/, feature: 'planner', screen: 'plan_list' },
  { pattern: /^\/plans\/[^/]+\/weeks\/?$/, feature: 'planner', screen: 'weekly_plan' },
  { pattern: /^\/daily-logs\/?$/, feature: 'daily_log', screen: 'daily_log' },
  { pattern: /^\/reports\/?$/, feature: 'reports', screen: 'report_list' },
  { pattern: /^\/reports\/new\/?$/, feature: 'reports', screen: 'report_create' },
  { pattern: /^\/reports\/[^/]+\/edit\/?$/, feature: 'reports', screen: 'report_edit' },
  { pattern: /^\/reports\/[^/]+\/?$/, feature: 'reports', screen: 'report_detail' },
  { pattern: /^\/routine-goals\/?$/, feature: 'routine_goals', screen: 'routine_goals' },
  { pattern: /^\/my-page\/privacy\/?$/, feature: 'privacy', screen: 'consent_settings' },
  { pattern: /^\/my-page\/?$/, feature: 'profile', screen: 'profile' },
  { pattern: /^\/policy-consent\/?$/, feature: 'legal', screen: 'required_consent' },
])

const API_RULES = Object.freeze([
  { pattern: /^\/images\/upload-urls$/, feature: 'image_upload', resource: 'image_upload' },
  { pattern: /^\/attendance-proofs(?:\/[^/]+)?$/, feature: 'attendance', resource: 'attendance_proof' },
  { pattern: /^\/coaching\/conversations(?:\/[^/]+)?(?:\/messages)?$/, feature: 'coaching', resource: 'coaching' },
  { pattern: /^\/mentor\/conversations(?:\/[^/]+)?(?:\/messages)?$/, feature: 'coaching', resource: 'coaching' },
  { pattern: /^\/daily-logs\/analytics\//, feature: 'daily_log', resource: 'daily_log_analytics' },
  { pattern: /^\/daily-logs(?:\/[^/]+)?$/, feature: 'daily_log', resource: 'daily_log' },
  { pattern: /^\/legal-documents\//, feature: 'legal', resource: 'legal_document' },
  { pattern: /^\/members\/me\/goals\//, feature: 'routine_goals', resource: 'routine_goal' },
  { pattern: /^\/members\/me\/withdraw(?:al-reasons)?$/, feature: 'profile', resource: 'account_withdrawal' },
  { pattern: /^\/members\/me(?:\/check-nickname)?$/, feature: 'profile', resource: 'profile' },
  { pattern: /^\/members(?:\/email|\/check-nickname)$/, feature: 'auth', resource: 'membership' },
  { pattern: /^\/auth\/login\/email$/, feature: 'auth', resource: 'login' },
  { pattern: /^\/auth\/email-verifications(?:\/codes)?$/, feature: 'auth', resource: 'email_verification' },
  { pattern: /^\/auth\/(?:me\/password|password-reset)$/, feature: 'auth', resource: 'password' },
  { pattern: /^\/reports(?:\/[^/]+)?(?:\/feedback)?$/, feature: 'reports', resource: 'report' },
  { pattern: /^\/reports\//, feature: 'reports', resource: 'report_reference' },
  { pattern: /^\/sessional-plans\/analytics\//, feature: 'planner', resource: 'plan_analytics' },
  { pattern: /^\/sessional-plans\//, feature: 'planner', resource: 'plan' },
  { pattern: /^\/sessional-plans$/, feature: 'planner', resource: 'plan' },
])

function getRequestPath(config = {}) {
  try {
    return new URL(config.url, 'https://api.akive.net').pathname
  } catch {
    return config.url?.split('?')[0] || ''
  }
}

function getApiDefinition(config = {}) {
  const path = getRequestPath(config)
  const rule = API_RULES.find(candidate => candidate.pattern.test(path))
  if (!rule) return null

  const method = String(config.method || 'get').toLowerCase()
  const verb = method === 'get' ? 'read'
    : method === 'post' ? 'create'
      : method === 'delete' ? 'delete'
        : 'update'

  return {
    action: `${verb}_${rule.resource}`,
    feature: rule.feature,
    method,
  }
}

function capture(eventName, properties) {
  return capturePosthogEvent(eventName, sanitizePosthogProperties({
    ...properties,
    app_version: APP_VERSION,
  }))
}

export function trackScreenView(pathname) {
  const definition = SCREEN_RULES.find(rule => rule.pattern.test(pathname))
  if (!definition) return false

  return capture(POSTHOG_EVENT.SCREEN_VIEWED, definition)
}

export function trackFeatureAction({ action, feature, outcome = 'success' }) {
  return capture(POSTHOG_EVENT.FEATURE_ACTION, {
    action,
    feature,
    outcome,
  })
}

export function trackApiSuccess(response) {
  const definition = getApiDefinition(response?.config)
  if (!definition || !HTTP_WRITE_METHODS.has(definition.method)) return false

  return capture(POSTHOG_EVENT.API_OPERATION_COMPLETED, {
    action: definition.action,
    feature: definition.feature,
    http_status: response?.status,
    outcome: 'success',
  })
}

export function trackApiFailure(error, config = error?.config) {
  if (error?.code === 'ERR_CANCELED') return false

  const definition = getApiDefinition(config)
  if (!definition) return false

  const errorCode = getApiErrorCode(error)
  if (definition.method === 'get' && EXPECTED_EMPTY_STATE_CODES.has(errorCode)) return false

  const status = error?.response?.status
  const errorType = ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code) ? 'timeout'
    : !error?.response && typeof navigator !== 'undefined' && navigator.onLine === false
      ? 'offline'
      : !error?.response ? 'network' : 'api'

  return capture(POSTHOG_EVENT.API_OPERATION_COMPLETED, {
    action: definition.action,
    error_code: errorCode,
    error_type: errorType,
    feature: definition.feature,
    http_status: status,
    outcome: 'failure',
  })
}

export function trackClientError({ errorCode, feature = 'app', source }) {
  return capture(POSTHOG_EVENT.CLIENT_ERROR, {
    error_code: errorCode,
    error_type: 'client',
    feature,
    outcome: 'failure',
    source,
  })
}

export function isAnalyticsEnabled() {
  return isPosthogCollectionEnabled()
}
